import { useEffect, useRef, useState } from 'preact/hooks';
import { ProposeCard } from './ProposeCard';
import { SourceCitationChip } from './SourceCitationChip';
import { InterpretationLabel } from './InterpretationLabel';
import {
  captureKnot,
  approveProposal,
  rejectProposal,
  ApiError,
  type CaptureContext,
  type CaptureResult,
} from '../lib/api';
import { bumpRecordsVersion } from '../lib/recordEditor';
import type { KnotState } from './Knot';
import './CaptureSheet.css';

export interface CaptureSheetProps {
  open: boolean;
  /** Real session data — the greeting names the signed-in person, never a
   *  placeholder. */
  name: string;
  /** The active workspace — every capture/approve/reject call is tenant-scoped. */
  tenantId: string;
  /** The page the Knot was pressed on (App derives it from the route). A file
   *  handed over while viewing a project/audition pre-associates with it. */
  context: CaptureContext;
  /** Drives the shared Knot renderer's state machine through the capture beats
   *  (App owns the knotState the nav's Knot reads): listening while composing,
   *  understanding in flight, proposing when the thread speaks, confirmed on
   *  approve. Real microphone amplitude is a LATER piece — the Knot synthesises
   *  a lively amp itself while listening this chunk. */
  onKnotState: (state: KnotState) => void;
  /** Closes the sheet AND returns the Knot to idle (App's closeSheet). Called on
   *  cancel, not-now, and after a confirmed capture settles. */
  onClose: () => void;
}

// How long the gold "confirmed" ripple lingers before the sheet closes itself.
const CONFIRM_HOLD_MS = 1100;

type Phase =
  | { step: 'compose' }
  | { step: 'submitting' }
  | { step: 'proposing'; result: CaptureResult }
  | { step: 'confirmed'; summary: string };

// The Knot's capture sheet — the "second door" into the same records manual
// CRUD creates. Reachable from every screen via the Knot (never gated behind a
// tab). No hint/instructional copy inside it (locked hard rule): the greeting
// is the assistant's own voice, and the controls teach themselves.
//
// One coherent flow, in-sheet: compose (a note + attached originals) -> hand it
// over -> the returned proposal renders as a propose->approve card here ->
// Approve files it (and refreshes the affected screen), Not now discards it.
// The manual path (record editor) stays fully usable — this is additive.
export function CaptureSheet({ open, name, tenantId, context, onKnotState, onClose }: CaptureSheetProps) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>({ step: 'compose' });
  const [error, setError] = useState<string | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Opening the sheet resets it to a blank compose step and starts the Knot
  // listening; closing clears any pending confirm timer. The Knot's OTHER
  // states are driven inline by the flow below.
  useEffect(() => {
    if (open) {
      setPhase({ step: 'compose' });
      setError(null);
      onKnotState('listening');
    }
    return () => {
      if (confirmTimer.current) {
        clearTimeout(confirmTimer.current);
        confirmTimer.current = null;
      }
    };
    // onKnotState is stable (App's setState); intentionally keyed on `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function reset(): void {
    setText('');
    setFiles([]);
    setPhase({ step: 'compose' });
    setError(null);
  }

  function close(): void {
    reset();
    onClose();
  }

  function onPickFiles(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const picked = Array.from(input.files);
    setFiles((prev) => [...prev, ...picked]);
    // Clear the native input so re-picking the SAME file still fires onChange.
    input.value = '';
  }

  function removeFile(index: number): void {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  const canSubmit = text.trim().length > 0 || files.length > 0;

  async function submit(): Promise<void> {
    if (!canSubmit || phase.step === 'submitting') return;
    setError(null);
    setPhase({ step: 'submitting' });
    onKnotState('understanding');
    try {
      const result = await captureKnot(tenantId, text, files, context);
      setPhase({ step: 'proposing', result });
      onKnotState('proposing');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not hand that over just now.');
      // Back to composing — the note + files are still here to retry.
      setPhase({ step: 'compose' });
      onKnotState('listening');
    }
  }

  async function approve(proposalId: string, summary: string): Promise<void> {
    setError(null);
    try {
      await approveProposal(tenantId, proposalId);
      // Refresh every read surface so the affected screen picks up the change.
      bumpRecordsVersion();
      onKnotState('confirmed');
      setPhase({ step: 'confirmed', summary });
      confirmTimer.current = setTimeout(() => {
        confirmTimer.current = null;
        close();
      }, CONFIRM_HOLD_MS);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not file that just now.');
    }
  }

  async function notNow(proposalId: string): Promise<void> {
    setError(null);
    try {
      await rejectProposal(tenantId, proposalId);
    } catch {
      // A failed reject is non-fatal — the pending proposal simply lingers in
      // the queue; the sheet still closes so the actor isn't trapped.
    } finally {
      close();
    }
  }

  return (
    <div class={`capture-sheet${open ? ' capture-sheet--open' : ''}`} aria-hidden={!open}>
      {phase.step === 'proposing' ? (
        <div class="capture-sheet__proposal">
          <ProposeCard
            citation={<SourceCitationChip glyph="◆" source="from the Knot" when="just now" live />}
            title={phase.result.preview.summary}
            meta={
              <InterpretationLabel kind="interpreted">
                {phase.result.preview.sourceIds.length > 0
                  ? `${phase.result.preview.sourceIds.length} file(s) retained`
                  : 'your note, captured'}
              </InterpretationLabel>
            }
            fileTarget={<>{captureKindLabel(phase.result.preview.captureKind)}</>}
            onApprove={() => void approve(phase.result.proposalId, phase.result.preview.summary)}
            onNotNow={() => void notNow(phase.result.proposalId)}
          />
          {error ? <div class="capture-sheet__error">{error}</div> : null}
        </div>
      ) : phase.step === 'confirmed' ? (
        <div class="capture-sheet__confirmed" role="status">
          <div class="capture-sheet__confirmed-mark" aria-hidden="true">
            ✓
          </div>
          <div class="capture-sheet__confirmed-text">{phase.summary}</div>
        </div>
      ) : (
        <>
          <div class="capture-sheet__title">What's up, {name}?</div>
          <textarea
            class="capture-sheet__input"
            value={text}
            disabled={phase.step === 'submitting'}
            onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
          />

          {files.length > 0 ? (
            <ul class="capture-sheet__chips">
              {files.map((file, i) => (
                <li key={`${file.name}-${i}`} class="capture-sheet__chip">
                  <span class="capture-sheet__chip-name">{file.name}</span>
                  <button
                    type="button"
                    class="capture-sheet__chip-x"
                    aria-label={`Remove ${file.name}`}
                    disabled={phase.step === 'submitting'}
                    onClick={() => removeFile(i)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div class="capture-sheet__attach">
            {/* General picker (files app / photo library). On mobile the OS
                picker already offers the camera + files here. */}
            <label class="capture-sheet__attach-btn">
              <span aria-hidden="true">＋</span> Attach
              <input
                type="file"
                class="capture-sheet__file"
                accept="application/pdf,image/*,audio/*"
                multiple
                onChange={onPickFiles}
              />
            </label>
            {/* Dedicated capture affordances — `capture` steers mobile straight
                to the camera / mic so they're OFFERED, not just the file app. */}
            <label class="capture-sheet__attach-btn capture-sheet__attach-btn--icon" aria-label="Take a photo">
              <span aria-hidden="true">◎</span>
              <input
                type="file"
                class="capture-sheet__file"
                accept="image/*"
                capture="environment"
                onChange={onPickFiles}
              />
            </label>
            <label class="capture-sheet__attach-btn capture-sheet__attach-btn--icon" aria-label="Record a voice note">
              <span aria-hidden="true">◑</span>
              <input
                type="file"
                class="capture-sheet__file"
                accept="audio/*"
                capture="user"
                onChange={onPickFiles}
              />
            </label>
          </div>

          {error ? <div class="capture-sheet__error">{error}</div> : null}

          <div class="capture-sheet__row">
            <button
              type="button"
              class="capture-sheet__btn capture-sheet__btn--approve"
              disabled={!canSubmit || phase.step === 'submitting'}
              onClick={() => void submit()}
            >
              {phase.step === 'submitting' ? 'Handing it over…' : 'Hand it over'}
            </button>
            <button
              type="button"
              class="capture-sheet__btn capture-sheet__btn--ghost"
              disabled={phase.step === 'submitting'}
              onClick={close}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// The backend's captureKind onto a short, honest filing-target phrase for the
// propose card. Never invents a specific record name — the deterministic
// backend routes by context, and this names the destination in that voice.
function captureKindLabel(kind: string): string {
  switch (kind) {
    case 'attach_to_subject':
      return 'Attach to what you were viewing';
    case 'note_on_subject':
      return 'Add as a note to what you were viewing';
    case 'generic_inbox':
    default:
      return 'Keep in your inbox';
  }
}
